import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';
import { ITrackingDB } from 'type/tracking';

import { useGetTrackingList } from 'hooks/queries/tracking';
import CoreSelect from 'shared/CoreSelect';
import CoreSvg from 'shared/CoreSvg';
import Icon from 'shared/icons';
import style from 'styles/styled-components/style';
import {
	ALL_GROUP,
	NO_GROUP,
	extractGroupList,
	extractGroupName,
	filterByGroup,
} from 'utils/trackingGroup';

interface ITrackingBreadcrumb {
	code: string;
	name: string;
}

const TrackingBreadcrumb = ({ code, name }: ITrackingBreadcrumb) => {
	const navigate = useNavigate();
	const { data: trackingList } = useGetTrackingList();

	const groupList = useMemo(
		() => extractGroupList(trackingList ?? []),
		[trackingList],
	);

	// '전체' 제외 그룹이 1개 이상이면 그룹 셀렉트 표시
	const hasGroups = groupList.length > 1;

	const currentGroup = extractGroupName(name);
	const [selectedGroup, setSelectedGroup] = useState(currentGroup);

	// 그룹 필터된 옵션 목록
	const filteredOptions = useMemo(() => {
		if (!hasGroups) {
			return trackingList?.map((item: ITrackingDB) => item.name) ?? [];
		}
		const filtered = filterByGroup(trackingList ?? [], selectedGroup);
		return filtered.map((item: ITrackingDB) => item.name);
	}, [trackingList, selectedGroup, hasGroups]);

	const handleSelectChange = (selectedName: string) => {
		const selected = trackingList?.find(
			(item: ITrackingDB) => item.name === selectedName,
		);
		if (selected) {
			navigate(`/tracking/${selected.code}`);
		}
	};

	const handleGroupChange = (group: string) => {
		setSelectedGroup(group);
		// 그룹 변경 시 해당 그룹의 첫 번째 항목으로 이동
		const filtered = filterByGroup(trackingList ?? [], group);
		if (filtered.length > 0) {
			navigate(`/tracking/${filtered[0].code}`);
		}
	};

	// 그룹 셀렉트용 옵션 ('전체' 제외)
	const groupOptions = groupList.filter((g) => g !== ALL_GROUP);

	return (
		<BreadcrumbWrapper>
			<Grid flex="start" align_items="center" _gap="8px">
				<BreadcrumbLink onClick={() => navigate('/tracking')}>
					콘텐츠 트래킹
				</BreadcrumbLink>
				<CoreSvg>
					<Icon.ArrowRight />
				</CoreSvg>

				{hasGroups && (
					<>
						<CoreSelect
							select_type="box"
							option_list={groupOptions}
							selected_option={selectedGroup}
							setSelectedOption={handleGroupChange}
							_width="200px"
							_height="36px"
						/>
						<CoreSvg>
							<Icon.ArrowRight />
						</CoreSvg>
					</>
				)}

				<CoreSelect
					select_type="box"
					option_list={filteredOptions}
					selected_option={name}
					setSelectedOption={handleSelectChange}
					_width="360px"
					_height="36px"
				/>
			</Grid>
		</BreadcrumbWrapper>
	);
};

const BreadcrumbWrapper = styled.div`
	width: 100%;
	display: flex;
	align-items: center;
	margin-bottom: 32px;
`;

const BreadcrumbLink = styled.span`
	${({ theme }) => theme.typo.body.p};
	color: ${({ theme }) => theme.colors.gray.gray50};
	cursor: pointer;

	&:hover {
		text-decoration: underline;
	}
`;

const { Grid } = style;

export default TrackingBreadcrumb;
